import Swiper from "swiper/bundle";

// Studio credit for anyone who opens DevTools.
console.log(
  "%cSite design & development by Applied Poetics%c\nhttps://ap.ltd",
  "font-weight: 600;",
  "font-weight: normal;"
);

const plpCardSwiperInstances = new WeakMap();

const parseMediaOrder = (orderString) => {
  if (!orderString) return [];
  return orderString
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
};

const getCardActiveView = (cardMedia) =>
  cardMedia && cardMedia.dataset.activeView === "model" ? "model" : "product";

const getCardMediaOrder = (cardMedia, view = getCardActiveView(cardMedia)) =>
  parseMediaOrder(
    view === "model" ? cardMedia?.dataset.modelOrder : cardMedia?.dataset.productOrder
  );

const getCardSlideCount = (cardMedia) => getCardMediaOrder(cardMedia).length;

const getActiveMediaIndex = (cardMedia, swiper) => {
  if (!swiper) return 0;
  const order = getCardMediaOrder(cardMedia);
  const activeSlide =
    swiper.slides && swiper.slides[swiper.activeIndex] ? swiper.slides[swiper.activeIndex] : null;
  const activeMediaId = activeSlide ? String(activeSlide.dataset.mediaId || "") : "";
  if (activeMediaId) {
    const orderIndex = order.indexOf(activeMediaId);
    if (orderIndex >= 0) return orderIndex;
  }
  return swiper.realIndex || 0;
};

const updateCardScrollbarThumb = (cardMedia) => {
  if (!cardMedia) return;
  const thumb = cardMedia.querySelector("[data-card-media-scrollbar-thumb]");
  if (!thumb) return;

  const slideCount = getCardSlideCount(cardMedia);
  if (slideCount <= 1) {
    thumb.style.transform = "translateX(0)";
    thumb.style.width = "100%";
    return;
  }

  const swiper = plpCardSwiperInstances.get(cardMedia);
  const activeIndex = getActiveMediaIndex(cardMedia, swiper);
  const clampedIndex = Math.max(0, Math.min(activeIndex, slideCount - 1));
  const slotWidth = 100 / slideCount;
  const leftPercent = clampedIndex * slotWidth;

  thumb.style.width = `${slotWidth}%`;
  thumb.style.left = `${leftPercent}%`;
  thumb.style.transform = "none";
};

const normalizeCardSlideIndex = (cardMedia, index) => {
  const slideCount = getCardSlideCount(cardMedia);
  if (slideCount <= 0) return 0;
  let targetIndex = index;
  if (targetIndex < 0) targetIndex = slideCount - 1;
  if (targetIndex >= slideCount) targetIndex = 0;
  return targetIndex;
};

const PLP_YT_PAUSE = '{"event":"command","func":"pauseVideo","args":""}';
const PLP_YT_PLAY = '{"event":"command","func":"playVideo","args":""}';

const pausePlpExternalIframesIn = (root) => {
  if (!root) return;
  root
    .querySelectorAll(
      'iframe[src*="youtube.com"],iframe[src*="youtube-nocookie.com"],iframe[src*="youtu.be"]',
    )
    .forEach((iframe) => {
      try {
        iframe.contentWindow?.postMessage(PLP_YT_PAUSE, "*");
      } catch (_e) {
        /* cross-origin */
      }
    });
  root.querySelectorAll('iframe[src*="player.vimeo.com"],iframe.js-vimeo').forEach((iframe) => {
    try {
      iframe.contentWindow?.postMessage('{"method":"pause"}', "*");
    } catch (_e) {
      /* cross-origin */
    }
  });
};

const playPlpExternalIframesIn = (root) => {
  if (!root) return;
  root
    .querySelectorAll(
      'iframe[src*="youtube.com"],iframe[src*="youtube-nocookie.com"],iframe[src*="youtu.be"]',
    )
    .forEach((iframe) => {
      try {
        iframe.contentWindow?.postMessage(PLP_YT_PLAY, "*");
      } catch (_e) {
        /* cross-origin */
      }
    });
  root.querySelectorAll('iframe[src*="player.vimeo.com"],iframe.js-vimeo').forEach((iframe) => {
    try {
      iframe.contentWindow?.postMessage('{"method":"play"}', "*");
    } catch (_e) {
      /* cross-origin */
    }
  });
};

const pauseAllPlpCardCarouselVideoMedia = (cardMedia) => {
  if (!cardMedia) return;
  cardMedia.querySelectorAll("video").forEach((v) => {
    v.pause();
    v.currentTime = 0;
  });
  pausePlpExternalIframesIn(cardMedia);
};

const syncPlpCardCarouselMedia = (cardMedia, swiper) => {
  // The init hook defers this a frame; a view reorder can destroy the instance
  // before it runs, leaving `swiper.slides` undefined.
  if (!cardMedia || !swiper || swiper.destroyed || !swiper.slides) return;

  // Any carousel movement (arrow nav, reset) dismisses the hover-preview overlay so
  // the real active slide is shown.
  cardMedia.classList.remove("plp-card-media--hovering");

  pauseAllPlpCardCarouselVideoMedia(cardMedia);

  const activeSlide = swiper.slides[swiper.activeIndex];
  if (!activeSlide || !activeSlide.classList.contains("plp-card-media-slide--video")) {
    return;
  }

  activeSlide.querySelectorAll("video").forEach((v) => {
    v.muted = true;
    v.loop = true;
    v.playsInline = true;
    v.play().catch(() => {});
  });
  playPlpExternalIframesIn(activeSlide);
};

const initCardMediaSwiper = (cardMedia, initialIndex = 0) => {
  if (!cardMedia) return;
  const track = cardMedia.querySelector("[data-card-media-track]");
  if (!track) return;

  const existing = plpCardSwiperInstances.get(cardMedia);
  if (existing) {
    existing.destroy(true, true);
    plpCardSwiperInstances.delete(cardMedia);
  }

  const slideCount = getCardSlideCount(cardMedia);
  const shouldLoop = slideCount > 1;
  const targetIndex = normalizeCardSlideIndex(cardMedia, initialIndex);
  const prev = cardMedia.querySelector("[data-card-media-prev]");
  const next = cardMedia.querySelector("[data-card-media-next]");

  const stopCardNavigation = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  // While a hover preview is showing, the carousel still sits on the view's first
  // slide but the overlay is previewing a different image. On the first arrow click we
  // instantly move the carousel onto the previewed image (masked by the overlay, which
  // shows that same image — so it's seamless) and then animate one step in the clicked
  // direction. The carousel therefore slides straight OUT of the previewed image: the
  // hover flows seamlessly into the carousel interaction, and the previewed image is
  // never re-shown as a destination. Bound before Swiper's nav handler so
  // stopImmediatePropagation lets us take over the click.
  const handleHoverNav = (direction) => (event) => {
    const sw = plpCardSwiperInstances.get(cardMedia);
    if (!sw || !cardMedia.classList.contains("plp-card-media--hovering")) return;

    const hoverId = cardMedia.dataset.hoverMediaId || "";
    if (!hoverId) return;
    const order = getCardMediaOrder(cardMedia);
    const hoverIndex = order.indexOf(hoverId);
    if (hoverIndex < 0 || order.length < 2) return;

    const currentIndex = getActiveMediaIndex(cardMedia, sw);
    if (currentIndex === hoverIndex) return; // already on the previewed slide — let Swiper run

    event.preventDefault();
    event.stopImmediatePropagation(); // take over this click from Swiper's own nav
    if (sw.params.loop) sw.slideToLoop(hoverIndex, 0);
    else sw.slideTo(hoverIndex, 0);
    requestAnimationFrame(() => {
      if (direction === "prev") sw.slidePrev();
      else sw.slideNext();
    });
  };

  [prev, next].forEach((arrow) => {
    if (!arrow) return;
    arrow.setAttribute("role", "button");
    arrow.setAttribute("tabindex", "0");
    if (arrow.dataset.navBound !== "true") {
      arrow.dataset.navBound = "true";
      arrow.addEventListener("click", stopCardNavigation);
      if (arrow === next) arrow.addEventListener("click", handleHoverNav("next"));
      if (arrow === prev) arrow.addEventListener("click", handleHoverNav("prev"));
      arrow.addEventListener("keydown", (event) => {
        if (event.key === "Enter" || event.key === " ") stopCardNavigation(event);
      });
    }
  });

  if (prev) prev.setAttribute("aria-label", "Previous media");
  if (next) next.setAttribute("aria-label", "Next media");

  const instance = new Swiper(cardMedia, {
    slidesPerView: 1,
    spaceBetween: 0,
    roundLengths: true,
    loop: shouldLoop,
    speed: 350,
    grabCursor: slideCount > 1,
    watchOverflow: true,
    allowTouchMove: slideCount > 1,
    simulateTouch: slideCount > 1,
    touchStartPreventDefault: false,
    navigation: {
      prevEl: prev,
      nextEl: next,
    },
    on: {
      init: function () {
        if (slideCount > 1) {
          if (shouldLoop) {
            this.slideToLoop(targetIndex, 0, false);
          } else {
            this.slideTo(targetIndex, 0, false);
          }
        }
        updateCardScrollbarThumb(cardMedia);
        requestAnimationFrame(() => syncPlpCardCarouselMedia(cardMedia, this));
      },
      slideChange: function () {
        syncPlpCardCarouselMedia(cardMedia, this);
        updateCardScrollbarThumb(cardMedia);
      },
    },
  });

  plpCardSwiperInstances.set(cardMedia, instance);
  updateCardScrollbarThumb(cardMedia);
};

const reorderCardMediaByView = (cardMedia, view) => {
  const track = cardMedia.querySelector("[data-card-media-track]");
  if (!track) return;

  pauseAllPlpCardCarouselVideoMedia(cardMedia);

  const existing = plpCardSwiperInstances.get(cardMedia);
  if (existing) {
    existing.destroy(true, true);
    plpCardSwiperInstances.delete(cardMedia);
  }

  const order = getCardMediaOrder(cardMedia, view);
  const slidesById = {};
  track.querySelectorAll(".plp-card-media-slide[data-media-id]").forEach((slide) => {
    const mediaId = String(slide.dataset.mediaId || "");
    if (mediaId) slidesById[mediaId] = slide;
  });

  order.forEach((mediaId) => {
    if (slidesById[mediaId]) track.appendChild(slidesById[mediaId]);
  });

  const fallbackFirst = view === "model" ? cardMedia.dataset.modelFirstId : cardMedia.dataset.productFirstId;
  const firstId = fallbackFirst || cardMedia.dataset.defaultMediaId || "";
  const firstSlide = firstId
    ? track.querySelector(`.plp-card-media-slide[data-media-id="${firstId}"]`)
    : null;
  const firstIndex = firstSlide ? Array.from(track.querySelectorAll(".plp-card-media-slide")).indexOf(firstSlide) : 0;

  initCardMediaSwiper(cardMedia, firstIndex >= 0 ? firstIndex : 0);
};

const updatePlpViewButtons = (view) => {
  const productButtons = document.querySelectorAll(".view-button.product-view");
  const modelButtons = document.querySelectorAll(".view-button.model-view");

  productButtons.forEach((button) => {
    const isActive = view === "product";
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-pressed", isActive ? "true" : "false");
  });

  modelButtons.forEach((button) => {
    const isActive = view === "model";
    button.classList.toggle("is-active", isActive);
    button.setAttribute("aria-pressed", isActive ? "true" : "false");
  });
};

const applyPlpMediaView = (view) => {
  const normalizedView = view === "model" ? "model" : "product";
  document.querySelectorAll(".plp-card-media").forEach((cardMedia) => {
    reorderCardMediaByView(cardMedia, normalizedView);
    cardMedia.dataset.activeView = normalizedView;
    updateCardScrollbarThumb(cardMedia);
  });
  updatePlpViewButtons(normalizedView);
};

const ensureCardHoverOverlay = (cardMedia) => {
  let overlay = cardMedia.querySelector(":scope > .plp-card-hover-image");
  if (!overlay) {
    overlay = document.createElement("img");
    overlay.className = "plp-card-hover-image";
    overlay.setAttribute("alt", "");
    overlay.setAttribute("aria-hidden", "true");
    overlay.decoding = "async";
    cardMedia.appendChild(overlay);
  }
  return overlay;
};

// Used only to reset the carousel on mouse-leave — speed 0 so the revert is an
// instant snap with no visible carousel slide animation.
const slideCardToOrderIndex = (cardMedia, swiper, index) => {
  if (!swiper) return;
  const count = getCardSlideCount(cardMedia);
  if (count <= 0) return;
  const target = Math.max(0, Math.min(index, count - 1));
  if (swiper.params.loop) swiper.slideToLoop(target, 0);
  else swiper.slideTo(target, 0);
};

// Desktop hover preview: fade in the "other view" image as an overlay WITHOUT moving
// the carousel. In Product view, hovering a card reveals its Model (main) image; in
// Model view it reveals the Ghost image. Navigating the carousel dismisses the preview
// (see syncPlpCardCarouselMedia and handleHoverNav), and leaving the card fades it out
// and resets to the pre-hover slide.
const bindCardHoverSwap = (cardMedia) => {
  if (!cardMedia || cardMedia.dataset.hoverSwapBound === "true") return;
  cardMedia.dataset.hoverSwapBound = "true";

  const overlay = ensureCardHoverOverlay(cardMedia);
  // Carousel slide showing when the mouse entered; restored on leave.
  let preHoverIndex = null;

  cardMedia.addEventListener("mouseenter", () => {
    const swiper = plpCardSwiperInstances.get(cardMedia);
    if (!swiper || getCardSlideCount(cardMedia) <= 1) return;

    // Product view previews the model (main) image; model view previews the ghost.
    const targetRole = getCardActiveView(cardMedia) === "model" ? "ghost" : "main";
    const targetSlide =
      cardMedia.querySelector(
        `.plp-card-media-slide[data-media-role="${targetRole}"]:not(.swiper-slide-duplicate)`,
      ) || cardMedia.querySelector(`.plp-card-media-slide[data-media-role="${targetRole}"]`);
    const targetImg = targetSlide ? targetSlide.querySelector("img") : null;
    if (!targetImg) return; // no opposite-view image to preview

    const src = targetImg.currentSrc || targetImg.getAttribute("src") || "";
    if (!src) return;
    if (overlay.getAttribute("src") !== src) overlay.setAttribute("src", src);

    preHoverIndex = getActiveMediaIndex(cardMedia, swiper);
    // Remember exactly which media the overlay is previewing, so the arrow handler can
    // start the carousel from that specific image by id (wherever it sits).
    cardMedia.dataset.hoverMediaId = String(targetSlide.dataset.mediaId || "");
    cardMedia.classList.add("plp-card-media--hovering");
  });

  cardMedia.addEventListener("mouseleave", () => {
    cardMedia.classList.remove("plp-card-media--hovering");
    delete cardMedia.dataset.hoverMediaId;
    const swiper = plpCardSwiperInstances.get(cardMedia);
    if (swiper && preHoverIndex !== null) {
      slideCardToOrderIndex(cardMedia, swiper, preHoverIndex);
    }
    preHoverIndex = null;
  });
};

// Hover swap is desktop-only: skip entirely on touch / non-hover devices.
const plpHoverSwapEnabled = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches;

const initPlpProductCardMedia = () => {
  // Always start in Product view on each page load / PLP navigation (no session persistence).
  const view = "product";
  const enableHoverSwap = plpHoverSwapEnabled();

  document.querySelectorAll(".plp-card-media").forEach((cardMedia) => {
    initCardMediaSwiper(cardMedia, 0);
    if (enableHoverSwap) bindCardHoverSwap(cardMedia);
  });

  const bindViewButton = (selector, viewValue) => {
    document.querySelectorAll(selector).forEach((button) => {
      if (button.dataset.plpViewBound === "true") return;
      button.dataset.plpViewBound = "true";
      button.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        applyPlpMediaView(viewValue);
      });
    });
  };

  bindViewButton(".view-button.product-view", "product");
  bindViewButton(".view-button.model-view", "model");
  applyPlpMediaView(view);
};

window.initPlpProductCardMedia = initPlpProductCardMedia;

const initCollectionLookbook = () => {
  const wrapper = document.getElementById("collection-lookbook");
  const gallery = document.getElementById("collection-lookbook-gallery");

  if (!wrapper || !gallery || gallery.dataset.initialized === "true") return;

  const backButton = document.getElementById("back-collection-lookbook");
  const closeButton = document.getElementById("close-collection-lookbook");
  const counter = wrapper.querySelector("[data-lookbook-counter]");
  const prevButton = wrapper.querySelector(".collection-lookbook__prev");
  const nextButton = wrapper.querySelector(".collection-lookbook__next");
  const total = Number(wrapper.dataset.lookbookTotal);
  const canLoop = total > 1;

  const swiper = new Swiper(gallery, {
    slidesPerView: 1,
    speed: 100,
    effect: "fade",
    fadeEffect: { crossFade: true },
    allowTouchMove: true,
    loop: canLoop,
  });

  gallery.dataset.initialized = "true";

  const goToSlide = (index, speed = 0) => {
    if (canLoop) {
      swiper.slideToLoop(index, speed);
      return;
    }

    swiper.slideTo(index, speed);
  };

  const updateCounter = () => {
    if (!counter || Number.isNaN(total) || total < 1) return;
    const currentIndex = canLoop ? swiper.realIndex + 1 : swiper.activeIndex + 1;
    const value = counter.querySelector("[data-lookbook-counter-value]");

    if (value) {
      value.textContent = `${currentIndex} of ${total}`;
      return;
    }

    counter.textContent = `Look ${currentIndex} of ${total}`;
  };

  const open = (index) => {
    goToSlide(index);
    updateCounter();
    wrapper.style.visibility = "visible";
    requestAnimationFrame(() => {
      wrapper.classList.add("is-visible");
      wrapper.setAttribute("aria-hidden", "false");
      wrapper.removeAttribute("inert");
    });
  };

  const close = () => {
    wrapper.classList.remove("is-visible");
    wrapper.setAttribute("aria-hidden", "true");
    wrapper.setAttribute("inert", "");
    window.setTimeout(() => {
      wrapper.style.visibility = "hidden";
      goToSlide(0);
    }, 100);
  };

  wrapper.setAttribute("inert", "");

  const collectionPage = document.querySelector(".collection-page");
  collectionPage?.addEventListener("click", (event) => {
    const zoom = event.target.closest(".collection-page__zoom");
    if (!zoom) return;

    const index = Number(zoom.dataset.lookbookIndex);
    if (Number.isNaN(index)) return;

    event.preventDefault();
    open(index);
  });

  backButton?.addEventListener("click", close);
  closeButton?.addEventListener("click", close);
  prevButton?.addEventListener("click", () => swiper.slidePrev());
  nextButton?.addEventListener("click", () => swiper.slideNext());
  gallery.addEventListener("click", () => {
    if (!wrapper.classList.contains("is-visible") || total < 2) return;
    swiper.slideNext();
  });
  swiper.on("slideChange", updateCounter);

  document.addEventListener("keydown", (event) => {
    if (!wrapper.classList.contains("is-visible")) return;

    if (event.key === "Escape") {
      close();
      return;
    }

    if (event.key === "ArrowRight") {
      swiper.slideNext();
      return;
    }

    if (event.key === "ArrowLeft") {
      swiper.slidePrev();
    }
  });
};

const initTheme = () => {
  initPlpProductCardMedia();
  initCollectionLookbook();
};

if (document.readyState === "loading") {
  window.addEventListener("DOMContentLoaded", initTheme);
} else {
  initTheme();
}
